/**
 * AdminProductEditor - Create/Edit Large Format Products
 * 
 * Features:
 * - Basic info: name, SKU, category, pricing
 * - Tabbed content: Description, Specifications, File Setup
 * - Size options with pricing
 * - Image upload
 */
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, ImagePlus, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Categories for dropdown
const CATEGORIES = [
  { id: 'banners', name: 'Banners', code: 'BAN' },
  { id: 'flags', name: 'Flags', code: 'FLG' },
  { id: 'banner_stands', name: 'Banner Stands', code: 'BNS' },
  { id: 'rigid', name: 'Rigid Signs', code: 'RGD' },
  { id: 'trade_show', name: 'Trade Show', code: 'TDP' },
  { id: 'seg', name: 'SEG Displays', code: 'SEG' },
  { id: 'tents', name: 'Tents', code: 'TNT' },
  { id: 'adhesive', name: 'Adhesive Products', code: 'ADH' },
  { id: 'wall', name: 'Wall Graphics', code: 'WAL' },
  { id: 'window', name: 'Window Graphics', code: 'WIN' },
  { id: 'floor', name: 'Floor Graphics', code: 'FLR' },
  { id: 'vehicle', name: 'Vehicle Graphics', code: 'VEH' },
  { id: 'canvas', name: 'Canvas Prints', code: 'CNV' },
  { id: 'wall_art', name: 'Wall Art', code: 'ART' },
  { id: 'photo', name: 'Photo Prints', code: 'PHO' },
  { id: 'accessories', name: 'Accessories', code: 'ACC' },
];

const AdminProductEditor = ({ productId, onBack, onSave }) => {
  const isNew = !productId;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  
  // Basic info
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [pricePerSqft, setPricePerSqft] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [leadTimeDays, setLeadTimeDays] = useState('3');
  
  // Tabbed content
  const [descriptionMain, setDescriptionMain] = useState('');
  const [descriptionSpec, setDescriptionSpec] = useState('');
  const [descriptionFileSetup, setDescriptionFileSetup] = useState('');
  
  // Size options
  const [sizeOptions, setSizeOptions] = useState([
    { name: '', sku: '', price: '' }
  ]);
  
  // Images
  const [imageUrl, setImageUrl] = useState('');
  const [images, setImages] = useState([]);

  // Fetch product if editing
  useEffect(() => {
    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/admin/${productId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Product not found');
      const result = await res.json();
      const data = result.product || result;
      
      // Populate form
      setName(data.name || '');
      setSku(data.sku || '');
      setCategoryId(data.category_id || '');
      setBasePrice(data.base_price?.toString() || '');
      setPricePerSqft(data.price_per_sqft?.toString() || '');
      setIsActive(data.is_active !== false);
      setLeadTimeDays(data.lead_time_days?.toString() || '3');
      
      // Description sections
      const sections = data.description_sections || {};
      setDescriptionMain(sections.main || data.description || '');
      setDescriptionSpec(sections.spec || '');
      setDescriptionFileSetup(sections.file_setup || '');
      
      // Size options
      if (data.size_options?.length > 0) {
        setSizeOptions(data.size_options.map(opt => ({
          name: opt.name || '',
          sku: opt.sku || '',
          price: opt.price?.toString() || ''
        })));
      }
      
      // Images
      setImageUrl(data.image_url || '');
      setImages(data.images || []);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!sku.trim()) {
      toast.error('SKU is required');
      return;
    }
    if (!categoryId) {
      toast.error('Please select a category');
      return;
    }
    
    setSaving(true);
    try {
      const category = CATEGORIES.find(c => c.id === categoryId);
      
      // Build product data
      const productData = {
        name: name.trim(),
        sku: sku.trim().toUpperCase(),
        category_id: categoryId,
        category_name: category?.name || '',
        category_code: category?.code || '',
        base_price: parseFloat(basePrice) || 0,
        price_per_sqft: parseFloat(pricePerSqft) || 0,
        is_active: isActive,
        lead_time_days: parseInt(leadTimeDays) || 3,
        product_type: 'LGF',
        
        // Description sections for tabs
        description_sections: {
          main: descriptionMain.trim(),
          spec: descriptionSpec.trim(),
          file_setup: descriptionFileSetup.trim()
        },
        
        // Also store combined description for search
        description: [descriptionMain, descriptionSpec, descriptionFileSetup]
          .filter(Boolean)
          .join('\n\n'),
        
        // Size options (filter out empty)
        size_options: sizeOptions
          .filter(opt => opt.name.trim())
          .map(opt => ({
            name: opt.name.trim(),
            sku: opt.sku.trim() || null,
            price: parseFloat(opt.price) || 0
          })),
        
        // Images
        image_url: imageUrl || images[0] || null,
        images: images.filter(Boolean)
      };
      
      let res;
      if (isNew) {
        res = await fetch(`${API_BASE_URL}/api/products/admin`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData)
        });
      } else {
        res = await fetch(`${API_BASE_URL}/api/products/admin/${productId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData)
        });
      }
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(isNew ? 'Product created!' : 'Product updated!');
      if (onSave) onSave();
      if (onBack) onBack();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Size option handlers
  const addSizeOption = () => {
    setSizeOptions([...sizeOptions, { name: '', sku: '', price: '' }]);
  };

  const removeSizeOption = (index) => {
    setSizeOptions(sizeOptions.filter((_, i) => i !== index));
  };

  const updateSizeOption = (index, field, value) => {
    const updated = [...sizeOptions];
    updated[index] = { ...updated[index], [field]: value };
    setSizeOptions(updated);
  };

  // Generate SKU from category and name
  const generateSku = () => {
    const category = CATEGORIES.find(c => c.id === categoryId);
    if (!category || !name) return;
    
    const nameSlug = name
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 4)
      .toUpperCase();
    const random = Math.random().toString(16).substring(2, 6).toUpperCase();
    
    setSku(`CA-LGF-${category.code}-${nameSlug}${random}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-product-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-semibold">
              {isNew ? 'Create Product' : 'Edit Product'}
            </h2>
            <p className="text-gray-500 text-sm">Large Format Products</p>
          </div>
        </div>
        
        <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {isNew ? 'Create Product' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., 13oz Vinyl Banner"
                  className="mt-1"
                  data-testid="product-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="sku">SKU *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="sku"
                    value={sku}
                    onChange={(e) => setSku(e.target.value.toUpperCase())}
                    placeholder="CA-LGF-XXX-XXXX"
                    className="font-mono"
                    data-testid="product-sku-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={generateSku}
                    disabled={!categoryId || !name}
                  >
                    Generate
                  </Button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger className="mt-1" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="basePrice">Base Price ($)</Label>
                <Input
                  id="basePrice"
                  type="number"
                  step="0.01"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="base-price-input"
                />
              </div>
              
              <div>
                <Label htmlFor="pricePerSqft">Price per Sq Ft ($)</Label>
                <Input
                  id="pricePerSqft"
                  type="number"
                  step="0.01"
                  value={pricePerSqft}
                  onChange={(e) => setPricePerSqft(e.target.value)}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="leadTime">Lead Time (days)</Label>
                <Input
                  id="leadTime"
                  type="number"
                  value={leadTimeDays}
                  onChange={(e) => setLeadTimeDays(e.target.value)}
                  placeholder="3"
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="isActive">Active (visible on storefront)</Label>
              </div>
            </div>
          </div>

          {/* Description Tabs */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold mb-4">Product Content</h3>
            
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="specifications">Specifications</TabsTrigger>
                <TabsTrigger value="filesetup">File Setup</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Label>Product Description</Label>
                <Textarea
                  value={descriptionMain}
                  onChange={(e) => setDescriptionMain(e.target.value)}
                  placeholder="Describe the product, its features, and benefits..."
                  rows={8}
                  className="mt-1"
                  data-testid="description-main"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Main product description shown on the product page.
                </p>
              </TabsContent>
              
              <TabsContent value="specifications" className="mt-4">
                <Label>Specifications</Label>
                <Textarea
                  value={descriptionSpec}
                  onChange={(e) => setDescriptionSpec(e.target.value)}
                  placeholder="• Material: 13oz vinyl&#10;• Finish: Matte/Gloss&#10;• Hemmed edges&#10;• Grommets included"
                  rows={8}
                  className="mt-1"
                  data-testid="description-spec"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Technical specifications, materials, and product details.
                </p>
              </TabsContent>
              
              <TabsContent value="filesetup" className="mt-4">
                <Label>File Setup Instructions</Label>
                <Textarea
                  value={descriptionFileSetup}
                  onChange={(e) => setDescriptionFileSetup(e.target.value)}
                  placeholder="Accepted formats: PDF, AI, PSD, JPG&#10;Resolution: 150 DPI minimum&#10;Color mode: CMYK&#10;Bleed: 0.125&quot; on all sides"
                  rows={8}
                  className="mt-1"
                  data-testid="description-filesetup"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Artwork requirements and file setup instructions for customers.
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* Size Options */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Size Options</h3>
              <Button variant="outline" size="sm" onClick={addSizeOption}>
                <Plus className="w-4 h-4 mr-1" />
                Add Size
              </Button>
            </div>
            
            <div className="space-y-3">
              {sizeOptions.map((opt, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Input
                    value={opt.name}
                    onChange={(e) => updateSizeOption(index, 'name', e.target.value)}
                    placeholder="Size name (e.g., 2ft x 4ft)"
                    className="flex-1"
                  />
                  <Input
                    value={opt.sku}
                    onChange={(e) => updateSizeOption(index, 'sku', e.target.value.toUpperCase())}
                    placeholder="SKU suffix"
                    className="w-32 font-mono"
                  />
                  <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={opt.price}
                      onChange={(e) => updateSizeOption(index, 'price', e.target.value)}
                      placeholder="0.00"
                      className="pl-7"
                    />
                  </div>
                  {sizeOptions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSizeOption(index)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-gray-500 mt-3">
              Add different size options with individual pricing. The first size price will be used as the default.
            </p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Image Upload */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold mb-4">Product Image</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="imageUrl">Image URL</Label>
                <Input
                  id="imageUrl"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
              
              {imageUrl && (
                <div className="relative">
                  <img
                    src={imageUrl.startsWith('/') ? `${API_BASE_URL}${imageUrl}` : imageUrl}
                    alt="Product preview"
                    className="w-full rounded-lg border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
              
              {!imageUrl && (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                  <ImagePlus className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-gray-500 text-sm">Enter image URL above</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Tips */}
          <div className="bg-blue-50 rounded-xl border border-blue-100 p-6">
            <h4 className="font-semibold text-blue-900 mb-2">Tips</h4>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Use the tabs to organize product info</li>
              <li>• Add multiple size options for flexibility</li>
              <li>• SKU format: CA-LGF-[CAT]-[CODE]</li>
              <li>• Base price is used when no size selected</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEditor;
